import { VideoFragment } from "../interfaces/VideoFragment";
import { jsonToAaf } from "./api";
import { Metadata } from "./video";


export function generateEDL(videoFile: string, fragments: VideoFragment[], metadata: Metadata): string {
    const fps: number = metadata.fps || 25;
    const fpsInt: number = Math.round(fps); // Используем целое значение fps
    const title: string = "Timeline 1"; //metadata.name || videoFile.split('/').pop() || "Imported Video";
    let edlContent: string = `TITLE: ${title}\nFCM: NON-DROP FRAME\n\n`;

    let recordIn: number = 0; // Начало записи первого клипа в 0 секунд
    let clipNumber: number = 1;
    
    fragments.forEach(fragment => {
        if (!fragment.visible) return; // Пропускаем невидимые фрагменты

        // Вычисляем длительность клипа в секундах
        const clipDuration: number = fragment.timestamp.end - fragment.timestamp.begin;
        // Временные метки исходного материала
        const sourceInSec: number = fragment.timestamp.begin;
        const sourceOutSec: number = fragment.timestamp.end;
        // Временные метки записи (record) в секвенции
        const recordOut: number = recordIn + clipDuration;

        // Форматируем номер клипа как "001", "002", ...
        const clipNumStr: string = clipNumber.toString().padStart(3, '0');
        // Преобразуем секунды в таймкод (HH:MM:SS:FF)
        const sourceInTC: string = secondsToTimecode(sourceInSec, fpsInt);
        const sourceOutTC: string = secondsToTimecode(sourceOutSec, fpsInt);
        const recordInTC: string = secondsToTimecode(recordIn, fpsInt);
        const recordOutTC: string = secondsToTimecode(recordOut, fpsInt);

        // Reel name задаём как "AX" (это стандартное поле для обозначения источника)
        edlContent += `${clipNumStr}  AX       V     C        ${sourceInTC} ${sourceOutTC} ${recordInTC} ${recordOutTC}\n`;
        const clipFileName = videoFile.split('/').pop() || "Unknown";
        edlContent += `* FROM CLIP NAME: ${clipFileName}\n\n`;
        
        recordIn = recordOut;
        clipNumber++;
    });
    
    return edlContent;
}

function secondsToTimecode(seconds: number, fps: number): string {
    // Переводим секунды в общее число кадров (округляем до целого)
    const totalFrames: number = Math.round(seconds * fps);
    const frames: number = totalFrames % fps;
    const totalSeconds: number = Math.floor(totalFrames / fps);
    const secs: number = totalSeconds % 60;
    const minutes: number = Math.floor(totalSeconds / 60) % 60;
    const hours: number = Math.floor(totalSeconds / 3600);
    // Форматируем таймкод с ведущими нулями: HH:MM:SS:FF
    return (
        String(hours).padStart(2, '0') + ":" +
        String(minutes).padStart(2, '0') + ":" +
        String(secs).padStart(2, '0') + ":" +
        String(frames).padStart(2, '0')
    );
}


export function generateXML(videoFile: string, fragments: VideoFragment[], metadata: Metadata): string {
    // Если у вас 23.976 или 23.98, DaVinci ожидает "1001/24000s" в frameDuration,
    // в остальных случаях сделаем упрощённо "1/25s", "1/24s" и т.п.
    const fps = metadata.fps || 25;
    const frameDuration = getFcpxFrameDuration(fps);

    // Размер кадра
    const width = metadata.width || 1920;
    const height = metadata.height || 1080;

    // Название проекта и имя для <asset>
    const projectName = metadata.name || "Project";
    // Уберём расширение и папки из videoFile, чтобы получить "demo" в качестве имени
    const assetBaseName = videoFile.split("/").pop()?.split(".")[0] || "Asset";

    // Считаем максимальную длину исходного файла (чтобы указать в duration у <asset>)
    let assetDuration = 0;
    fragments.forEach(fragment => {
        if (!fragment.visible) return;
        const endSec = fragment.timestamp.end - fragment.skip_delta;
        if (endSec > assetDuration) {
            assetDuration = endSec;
        }
    });
    // Если в metadata есть duration, берём максимум
    if (metadata.duration && metadata.duration > assetDuration) {
        assetDuration = metadata.duration;
    }

    // Формируем клипы на таймлайне (spine)
    let timelineDuration = 0;
    let clipsXML = "";
    let clipNumber = 1;

    fragments.forEach(fragment => {
        if (!fragment.visible) return;

        const clipDuration = fragment.timestamp.end - fragment.timestamp.begin;
        const assetStart = fragment.timestamp.begin - fragment.skip_delta; // откуда брать в исходном файле
        const offset = timelineDuration; // куда ставим на таймлайне

        // Добавляем XML-узел для клипа
        clipsXML += `                        <asset-clip name="Clip ${clipNumber}" `
                 + `offset="${formatSeconds(offset)}" `
                 + `ref="r2" `
                 + `start="${formatSeconds(assetStart)}" `
                 + `duration="${formatSeconds(clipDuration)}"/>\n`;

        timelineDuration += clipDuration;
        clipNumber++;
    });

    // Собираем итоговый XML
    const fcpxml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE fcpxml>
<fcpxml version="1.9">
    <resources>
        <format id="r1" name="FCPFormat" frameDuration="${frameDuration}" width="${width}" height="${height}"/>
        <asset id="r2"
               src="${videoFile}"
               name="${assetBaseName}"
               start="0s"
               duration="${formatSeconds(assetDuration)}"
               hasAudio="1"
               hasVideo="1"
               format="r1"/>
    </resources>
    <library>
        <event name="Event">
            <project name="${projectName}">
                <sequence duration="${formatSeconds(timelineDuration)}"
                          format="r1"
                          tcStart="0s"
                          audioLayout="stereo"
                          audioRate="48k">
                    <spine>
${clipsXML}                    </spine>
                </sequence>
            </project>
        </event>
    </library>
</fcpxml>`;

    return fcpxml;
}

/**
 * Возвращает frameDuration для FCPXML.
 * Для 23.976/23.98 возвращаем "1001/24000s".
 * В остальных случаях делаем упрощённо "1/<rounded_fps>s".
 */
function getFcpxFrameDuration(fps: number): string {
    // Проверяем, близко ли fps к 23.976
    const is23976 = Math.abs(fps - 23.976) < 0.01 || Math.abs(fps - 23.98) < 0.01;
    if (is23976) {
        // DaVinci Resolve ожидает 1001/24000s
        return "1001/24000s";
    } else {
        // Для простоты округлим до целого
        const rounded = Math.round(fps);
        return `1/${rounded}s`;
    }
}

/**
 * Форматируем число секунд в строку "X.XXXs" (3 знака после запятой).
 */
function formatSeconds(seconds: number): string {
    return seconds.toFixed(3) + "s";
}


export function generateJSX(videoFile: string, fragments: VideoFragment[], metadata: Metadata) {
    console.log("generating jsx", videoFile, fragments, metadata);

    let jsxContent = `
app.beginUndoGroup("Import Video");

var comp = app.project.items.addComp("${metadata.name || 'Imported Video'}", ${metadata.width || 1920}, ${metadata.height || 1080}, 1, ${metadata.duration ?? 1000}, ${metadata.fps || 25});

var videoFile = new File("${videoFile.replace(/\\/g, "\\\\")}");
var importOptions = new ImportOptions(videoFile);
var video = app.project.importFile(importOptions);
`;

    fragments.slice().reverse().forEach((fragment, index) => {
        if (!fragment.visible) return;

        jsxContent += `
var clip${index + 1} = comp.layers.add(video);
clip${index + 1}.startTime = -${fragment.skip_delta}; 
clip${index + 1}.inPoint = ${fragment.timestamp.begin - fragment.skip_delta}; 
clip${index + 1}.outPoint = ${fragment.timestamp.end - fragment.skip_delta};
`;
    });

    jsxContent += `
app.endUndoGroup();
`;

    return jsxContent;
}


//our  json fornat that we'll use to create AAF later
export function generateJSON(videoFile: string, fragments: any[], metadata: Metadata): any {
    const fps: number = metadata.fps || 25;
    const extension = videoFile.split('.').pop()?.toLowerCase() || '';
    const baseName = videoFile.split('/').pop() || '';
    const reelName = metadata.name
        ? metadata.name.split('.').slice(0, -1).join('.')
        : baseName.split('.').slice(0, -1).join('.');

    // Compute asset duration in seconds
    let assetDurationSec = metadata.duration ?? 0;
    if (metadata.duration == null) {
        fragments.forEach(fragment => {
            if (!fragment.visible) return;
            const endSec = fragment.timestamp.end - (fragment.skip_delta || 0);
            if (endSec > assetDurationSec) {
                assetDurationSec = endSec;
            }
        });
    }


    const source = {
        reel_name: reelName,
        name: baseName,
        format: extension,
        path: videoFile,
        umid: metadata.umid,
        start_timecode: metadata.timecodeStart,
        frame_rate: fps,
        width: metadata.width,
        height: metadata.height,
        frame_rate_num: metadata.frame_rate_num,
        frame_rate_den: metadata.frame_rate_den,
        duration: assetDurationSec,
        field_dominance: metadata.field_dominance,
        component_width: metadata.component_width,
        horizontal_subsampling: metadata.horizontal_subsampling,
        vertical_subsampling: metadata.horizontal_subsampling,
        video_line_map_1: metadata.video_line_map_1,
        video_line_map_2: metadata.video_line_map_2,
        aspect_ratio_num: metadata.aspect_ratio_num,
        aspect_ratio_den: metadata.aspect_ratio_den,
        codec: metadata.videoCodec,
        audio_channels: metadata.audioChannels,
        audio_rate: metadata.sampleRate,
        audio_depth: metadata.audioBitDepth,
        pixel_format: metadata.pixelFormat
    };

    // Build the sequence array
    const sequenceArr = fragments
        .filter(fragment => fragment.visible)
        .map(fragment => {
            const startFrame = Math.round((fragment.timestamp.begin - (fragment.skip_delta || 0)) * fps);
            const lengthFrames = Math.round((fragment.timestamp.end - fragment.timestamp.begin) * fps);
            //console.log(fragment, fragment, "startFrame", startFrame, "lengthFrames", lengthFrames);
            return {
                source: 0,
                start: startFrame,
                length: lengthFrames
            };
        });

    return {
        filename: baseName,
        sources: [source],
        sequence: sequenceArr
    };
}



export async function generateAAF(
    videoFile: string,
    fragments: any[],
    metadata: Metadata
  ): Promise<Blob> {
    const payload = generateJSON(videoFile, fragments, metadata);
    const response = await jsonToAaf(payload);
    if (response instanceof Blob) {
      return response;
    }
    throw("AAF generation error")
  }